import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { CardWrapper, ButtonOutlined } from '../../UI';
import { hasUserPermissions } from '../../../../helpers/utils/permissoes';
import MenuDemonstrativoPorTitulos from './MenuDemonstrativoPorTitulos';
import * as C from '../../../theme/colors.constants';

/** Códigos de sub-permissão do grupo "C/C de Cliente" (`menuRoutes.jsx`). */
const PERMISSOES_CC_CLIENTE = [1801, 1802, 1803, 1804];
/** Códigos de sub-permissão do grupo "C/C de Comissionado" (`menuRoutes.jsx`). */
const PERMISSOES_CC_COMISSIONADO = [1601, 1602, 1603, 1604];

/**
 * Card "Operações" da Ficha Financeira (AW2-1079) — extraído de
 * `AbaFinanceira.jsx` para isolar os 3 gates de permissão e o estado de menu
 * do Demonstrativo por Títulos (Etapa 4).
 *
 * "C/C CLIENTE" (Etapa 2): mesma rota/padrão de query param já usados por
 * `FichaDoCliente.jsx` (`src/components/Cliente/Crud/Tabs/FichaDoCliente/`).
 * Gate de UI é cosmético — a rota real já é protegida por
 * `PrivateRoute roles={['1801','1802','1803','1804']}` (`RoutesFinanceiro.jsx`)
 * e o endpoint por trás dela valida a permissão de novo no backend.
 * `idCliente` vem de `dadosProcesso.codigoCliente` (dado do processo já
 * carregado, não input livre) — `encodeURIComponent` aplicado por princípio
 * de defesa em profundidade antes de concatenar na URL.
 *
 * `dadosProcesso` só popula depois que `buscarAcompanhamentoDoProcessoService`
 * resolve (efeito assíncrono do container, `ControleProcessosPage.jsx`) — a
 * aba é renderizada sem esperar esse fetch, então `idCliente` pode estar
 * ausente mesmo para um usuário com a permissão. Botão fica desabilitado
 * até `idCliente` chegar, evitando abrir a rota com `idCliente=undefined`.
 *
 * "C/C COMISSIONADO" (Etapa 3): abre `/conta-corrente-comissionado` sem
 * query param — a pré-seleção "usuário logado é ele próprio comissionado"
 * já é resolvida inteiramente pelo backend/tela real
 * (`ContaCorrenteComissionadoPage.jsx`, flag `isOfUser` por comissionado),
 * disparada automaticamente no mount dela, independente de onde a aba foi
 * aberta — não há `idComissionado`/`isComissionado` na sessão do usuário
 * logado no frontend para replicar essa lógica aqui. Gate de UI cosmético,
 * igual ao de C/C Cliente — mas aqui ainda mais cosmético: a rota real
 * (`/conta-corrente-comissionado`, `RoutesFinanceiro.jsx`) usa
 * `roles={[]}` (libera qualquer usuário autenticado, `tipoUsuario='U'`),
 * não os códigos 1601-1604 — só o link do menu lateral usa esses códigos
 * (`menuRoutes.jsx`). Comportamento pré-existente de produção, fora do
 * escopo desta story.
 *
 * "DEMONSTRATIVO POR TÍTULO" (Etapa 4): extraído para
 * `MenuDemonstrativoPorTitulos.jsx` (não é um simples botão — tem o próprio
 * fetch, guard de sessão e Snackbar de erro). Sem gate de permissão (não
 * listado nas sub-permissões do Jira desta story).
 */
export default function OperacoesFichaFinanceira({ dadosProcesso, idProcesso }) {
  const idCliente = dadosProcesso?.codigoCliente;
  const podeAcessarCCCliente = hasUserPermissions(...PERMISSOES_CC_CLIENTE) && idCliente != null;
  const podeAcessarCCComissionado = hasUserPermissions(...PERMISSOES_CC_COMISSIONADO);

  return (
    <CardWrapper>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
      }}
      >
        <SettingsOutlinedIcon sx={{ color: C.navy }} />
        <Typography variant="h3">Operações</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <ButtonOutlined
          startIcon={<ArticleOutlinedIcon />}
          disabled={!podeAcessarCCCliente}
          onClick={() => window.open(`/conta-corrente-cliente/pesquisar?idCliente=${encodeURIComponent(idCliente)}`, '_blank')}
        >
          C/C CLIENTE
        </ButtonOutlined>
        <ButtonOutlined
          startIcon={<GroupOutlinedIcon />}
          disabled={!podeAcessarCCComissionado}
          onClick={() => window.open('/conta-corrente-comissionado', '_blank')}
        >
          C/C COMISSIONADO
        </ButtonOutlined>
        <MenuDemonstrativoPorTitulos idProcesso={idProcesso} />
      </Box>
    </CardWrapper>
  );
}
OperacoesFichaFinanceira.propTypes = {
  dadosProcesso: PropTypes.shape({
    codigoCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
OperacoesFichaFinanceira.defaultProps = {
  dadosProcesso: null,
  idProcesso: null,
};
